import { Injectable } from '@angular/core';

import { XmStore, XmStoreUtil } from 'src/xm/core/services/store';
import { StoreAction } from 'store/actions';

import { RequestStatus } from 'store/request-status/models';

import { PaymentState, PERCENTAGE_UPDATE_INTERVAL, ProgressStatus, REQUEST_INTERVAL, REQUEST_TIMEOUT, RETRY_REQUEST_COUNT } from 'my-account/constants';
import { RequestResult } from 'store/line/models';
import { AddonLines, AddRemovePassAction } from 'my-account/interface';
import { AddonRequestStatus } from 'store/global-travel-pass/models';
import { StateService } from '@uirouter/core';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class AddonsUtil {
    public postAddOnsPayload: AddRemovePassAction;
    public addonType: string;
    public addonRouteType: string;
    public addonAction: string;
    public addonLines: AddonLines = {};
    public pollingRequestId: string;
    public addonsPollingPercentage$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    public addonsPollingStatus$: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
    public addonsSwitchTimeout: NodeJS.Timer;

    private xmStore: XmStore;
    private state: StateService;
    private requestApiInterval: NodeJS.Timer;
    private switchTimeout: NodeJS.Timer;
    private addonsRequestApiInterval: NodeJS.Timer;
    private addonsPollingCount: number = 0;
    private lastAddonsOverallStatus: string;
    private percentageUpdateInterval: NodeJS.Timer;
    private currentPercentage: number = 0;
    private targetPercentage: number = 0;

    constructor(xmStore: XmStore, state: StateService) {
        Object.assign(this, { xmStore, state });
    }

    public postApis(storeActionName: string, lineId: string, planId?: string, removalRequestCode?: string): Promise<RequestStatus> {
        return XmStoreUtil.defaultCatch(this.xmStore.persist<RequestStatus>(storeActionName, { lineId, planId, removalRequestCode }));
    }

    public completeAddonsPolling(overallStatus?: string): void {
        // Emit the final status
        if (overallStatus) {
            this.addonsPollingStatus$.next(overallStatus);
        }
        // Persist the final status
        XmStoreUtil.defaultCatch(this.xmStore.persist<RequestResult>(StoreAction.SAVE_REQUEST_RESULT, { requestStatus: overallStatus }));
        clearInterval(this.addonsRequestApiInterval);
        clearTimeout(this.addonsSwitchTimeout);
        clearInterval(this.percentageUpdateInterval);
        this.addonsRequestApiInterval = undefined;
        this.addonsSwitchTimeout = undefined;
        this.percentageUpdateInterval = undefined;
        this.addonsPollingCount = 0;

        // Set to 100% when complete
        this.currentPercentage = 100;
        this.targetPercentage = 100;
        this.addonsPollingPercentage$.next(100);
    }

    public completePolling(requestStatus: string): void {
        XmStoreUtil.defaultCatch(this.xmStore.persist<RequestResult>(StoreAction.SAVE_REQUEST_RESULT, { requestStatus }));
        clearInterval(this.requestApiInterval);
        clearTimeout(this.switchTimeout);
        this.requestApiInterval = undefined;
        this.switchTimeout = undefined;
    }

    public initPolling(requestId: string, lineId: string): void {
        this.requestApiInterval = setInterval(() => {
            this.queryStatus(requestId).then((requestStatus: RequestStatus) => {
                if (requestStatus.status === ProgressStatus.SUCCESS) {
                    // if status is SUCCESS, complete Addon right away
                    this.completePolling(requestStatus.status);

                    // clear API's only for SUCCESS
                    this.clearApis(lineId);
                } else if (requestStatus.status === ProgressStatus.FAIL) {
                    // if status is FAIL, complete Addon right away
                    this.completePolling(requestStatus.status);
                } else if (!this.switchTimeout && requestStatus.status === ProgressStatus.IN_PROGRESS) {
                    // set timeout only once when got status IN_PROGRESS
                    this.switchTimeout = setTimeout(() => {
                        this.completePolling(requestStatus.status);
                    }, REQUEST_TIMEOUT);
                }
            }).catch(() => {
                this.completePolling(ProgressStatus.FAIL);
            });
        }, REQUEST_INTERVAL);
    }


    public initAddonsPolling(): void {
        this.addonsPollingStatus$.next(ProgressStatus.IN_PROGRESS);

        this.addonsPollingCount = 0;
        this.lastAddonsOverallStatus = ProgressStatus.IN_PROGRESS;

        // Reset percentage tracking
        this.currentPercentage = 0;
        this.targetPercentage = 0;
        this.addonsPollingPercentage$.next(0);

        // Start the smooth percentage update interval
        this.startPercentageUpdates();

        // Call the API immediately for the first time
        this.performAddonsPollingCall();

        // Set up interval for subsequent calls
        this.addonsRequestApiInterval = setInterval(() => {
            this.performAddonsPollingCall();
        }, REQUEST_INTERVAL);
    }

    private performAddonsPollingCall(): void {
        this.addonsPollingCount++;

        this.targetPercentage = Math.min((this.addonsPollingCount / RETRY_REQUEST_COUNT) * 100, 95);

        // Stop polling after 5 attempts
        if (this.addonsPollingCount > RETRY_REQUEST_COUNT) {
            this.completeAddonsPolling(this.lastAddonsOverallStatus);

            return;
        }

        this.queryAddonsRequestStatus().then((addonRequestStatus: AddonRequestStatus) => {
            this.lastAddonsOverallStatus = addonRequestStatus.overallStatus;

            if (addonRequestStatus.overallStatus === ProgressStatus.SUCCESS) {
                this.clearApis();
                this.state.go('account.activity.addon.review.success', { addon_type: this.addonRouteType, intent: this.addonAction });
            } else if (addonRequestStatus.overallStatus === ProgressStatus.FAIL) {
                this.clearApis();
                this.completeAddonsPolling(addonRequestStatus.overallStatus);
                this.state.go('account.activity.addon.review.failure',  { addon_type: this.addonRouteType, intent: this.addonAction });
            } else if (this.addonsPollingCount === RETRY_REQUEST_COUNT) {
                this.clearApis();
                // After 5 attempts, move to pending status
                this.state.go('account.activity.addon.review.pending', { addon_type: this.addonRouteType, intent: this.addonAction });
            } else if (!this.addonsSwitchTimeout && addonRequestStatus.overallStatus === ProgressStatus.IN_PROGRESS) {
                // Set timeout only once when got status IN_PROGRESS
                this.addonsSwitchTimeout = setTimeout(() => {
                    this.completeAddonsPolling(this.lastAddonsOverallStatus);
                }, REQUEST_TIMEOUT);
            }
        }).catch(() => {
            // On API error, still respect the polling count limit
            // If not at limit yet, continue polling (the interval will call again)
            this.lastAddonsOverallStatus = ProgressStatus.FAIL;
            
            if (this.addonsPollingCount >= RETRY_REQUEST_COUNT) {
                // If we've hit max retries, clean up and navigate to failure page
                this.clearApis();
                this.completeAddonsPolling(ProgressStatus.FAIL);
                this.state.go('account.activity.addon.review.failure', { addon_type: this.addonRouteType, intent: this.addonAction });
            }
        });
    }

    private startPercentageUpdates(): void {
        this.percentageUpdateInterval = setInterval(() => {
            if (this.currentPercentage < this.targetPercentage) {

                const incrementPerUpdate: number = 20 / (REQUEST_INTERVAL / PERCENTAGE_UPDATE_INTERVAL);

                this.currentPercentage = Math.min(this.currentPercentage + incrementPerUpdate, this.targetPercentage);
                this.addonsPollingPercentage$.next(Math.floor(this.currentPercentage));
            }
        }, PERCENTAGE_UPDATE_INTERVAL);
    }

    private queryStatus(requestId: string): Promise<RequestStatus> {
        return this.xmStore.queryPromise<RequestStatus>(StoreAction.GET_REQUEST_STATUS, { requestId });
    }

    private queryAddonsRequestStatus(): Promise<AddonRequestStatus> {
        return this.xmStore.queryPromise<AddonRequestStatus>(StoreAction.GET_ADDONS_REQUEST_STATUS, {
            requestId: this.pollingRequestId
        });
    }

    private clearApis(lineId?: string): void {
        // clear other relevant models from store
        const clearApisList: StoreDeleteAction[] = [
            { action: StoreAction.CLEAR_STATEMENT, filters: { paymentState: PaymentState.ESTIMATED } },
            { action: StoreAction.CLEAR_TRANSACTION },
            { action: StoreAction.CLEAR_GLOBAL_TRAVEL_PASS }
        ];

        if (lineId) {
            clearApisList.push({ action: StoreAction.CLEAR_ADDONS_COLLECTION, filters: { lineId } });
        }

        this.xmStore.clearApis(clearApisList);
    }
}
